"use client";

import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

type JourneySlide = {
  id: string;
  tab: string;
  content: ReactNode;
};

type CcHomeJourneyStoryProps = {
  tablistLabel: string;
  slides: JourneySlide[];
};

/** Time each slide stays before the story moves on. */
const SLIDE_MS = 7000;

/**
 * One-screen story: the slides crossfade in place, numbered tabs with progress
 * bars show where you are, and the story advances by itself once it's on
 * screen. Keyboard focus pauses it; reduced motion never autoplays.
 */
export function CcHomeJourneyStory({ tablistLabel, slides }: CcHomeJourneyStoryProps) {
  const baseId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useCcPrefersReducedMotion();
  const [active, setActive] = useState(0);
  const [inView, setInView] = useState(false);
  const [paused, setPaused] = useState(false);
  const [seen, setSeen] = useState(false);
  const playing = inView && !paused && !reducedMotion;

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) setSeen(true);
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setTimeout(() => setActive((index) => (index + 1) % slides.length), SLIDE_MS);
    return () => window.clearTimeout(timer);
  }, [active, playing, slides.length]);

  const select = useCallback((index: number) => setActive(index), []);

  return (
    <div
      ref={rootRef}
      className="cc-journey__stage"
      data-playing={playing ? "true" : undefined}
      onFocus={(event) => {
        if (event.target.matches(":focus-visible")) setPaused(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
      style={{ "--cc-journey-slide-ms": `${SLIDE_MS}ms` } as CSSProperties}
    >
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          id={`${baseId}-panel-${index}`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-${index}`}
          aria-hidden={index !== active}
          className={`cc-journey__panel cc-journey__panel--${slide.id}`}
          data-current={index === active ? "true" : undefined}
          data-active={seen && index === active ? "true" : undefined}
        >
          {slide.content}
        </div>
      ))}

      <div className="cc-journey__tabs" role="tablist" aria-label={tablistLabel}>
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            id={`${baseId}-tab-${index}`}
            type="button"
            role="tab"
            aria-selected={index === active}
            aria-controls={`${baseId}-panel-${index}`}
            className="cc-journey__tab"
            onClick={() => select(index)}
          >
            <span className="cc-journey__tab-label">
              <span className="cc-journey__tab-number">{String(index + 1).padStart(2, "0")}</span>
              {slide.tab}
            </span>
            <span className="cc-journey__tab-bar" aria-hidden="true">
              <span key={`${index}-${active}`} data-state={index < active ? "done" : index === active ? "current" : "next"} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
